/**
 * Excel（.xlsx / .xls）ファイルをブラウザ内で CSV ファイルに変換するユーティリティ。
 * ダッシュボードの CSV インポート API（/api/todolists/{id}/import-csv）は
 * テキスト（CSV）のみ受け付けるため、Excel 選択時はここで変換してから送信する。
 */

/** ファイル名が Excel 形式（.xlsx / .xls）かどうかを判定する */
export const isExcelFile = (fileName: string): boolean =>
  /\.(xlsx|xls)$/i.test(fileName);

/**
 * Excel ファイルの先頭シートを CSV に変換した File を返す。
 * 日付セルはタイムゾーンの影響を受けないよう、シリアル値から
 * 直接 "yyyy-mm-dd" 文字列へ変換する（バックエンドの期待形式）。
 */
export const convertExcelToCsvFile = async (file: File): Promise<File> => {
  // xlsx はサイズが大きいため、Excel 取込時のみ動的ロードする
  const XLSX = await import("xlsx");

  const buf = await file.arrayBuffer();
  // cellNF: true でセルの表示形式（cell.z）を保持し、日付セル判定に使う
  const workbook = XLSX.read(buf, { type: "array", cellNF: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error("シートが見つかりません。");
  const sheet = workbook.Sheets[sheetName];

  // 日付書式の数値セルを "yyyy-mm-dd" の文字列セルに置き換える
  const ref = sheet["!ref"];
  if (ref) {
    const range = XLSX.utils.decode_range(ref);
    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = sheet[addr];
        if (!cell || cell.t !== "n" || !cell.z) continue;
        if (XLSX.SSF.is_date(String(cell.z))) {
          cell.v = XLSX.SSF.format("yyyy-mm-dd", cell.v as number);
          cell.t = "s";
          delete cell.w;
          delete cell.z;
        }
      }
    }
  }

  const csv = XLSX.utils.sheet_to_csv(sheet);

  // UTF-8 BOM 付きで返す（サーバー側のエンコーディング自動判別と揃える）
  const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
  const csvName = file.name.replace(/\.(xlsx|xls)$/i, ".csv");
  return new File([bom, csv], csvName, { type: "text/csv" });
};
